import React, { useCallback, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { Button } from '../Button';

export interface ItemListItem {
  action?: React.ReactNode;
  icon: React.ReactNode;
  id: string;
  label: React.ReactNode;
}

export interface ItemListProps {
  items: ItemListItem[];
  showCount?: number;
}

const listStyles = {
  m: 0,
  p: 0,
  border: '1px solid',
  borderColor: 'softBorder',
  borderRadius: 1.5,
  overflow: 'hidden',
};
const rowStyles = {
  minHeight: 48,
  px: 1.25,
  py: 0.75,
  borderBottom: '1px solid',
  borderColor: 'softBorder',
  listStyle: 'none',
  '&:last-of-type': { borderBottom: 0 },
};
const iconStyles = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 1,
  bgcolor: 'action.hover',
  color: 'text.secondary',
  flexShrink: 0,
  '& > svg': { fontSize: '1.125rem' },
};
const labelStyles = { minWidth: 0, flexGrow: 1 };
const showAllRowStyles = {
  display: 'flex',
  justifyContent: 'center',
  p: 0.75,
  listStyle: 'none',
  bgcolor: 'action.hover',
};
const showAllButtonStyles = { minWidth: 140 };

const ItemListRow = React.memo(({ action, icon, label }: ItemListItem) => (
  <Stack
    component="li"
    direction="row"
    alignItems="center"
    gap={1.25}
    sx={rowStyles}
  >
    <Box sx={iconStyles}>{icon}</Box>
    <Typography noWrap variant="body2" sx={labelStyles}>
      {label}
    </Typography>
    {action}
  </Stack>
));

ItemListRow.displayName = 'ItemListRow';

const ItemList = React.memo(({ items, showCount }: ItemListProps) => {
  const [expanded, setExpanded] = useState(false);
  const visibleItems = useMemo(() => {
    if (expanded || showCount === undefined) {
      return items;
    }

    return items.slice(0, Math.max(0, showCount));
  }, [expanded, items, showCount]);
  const canExpand =
    !expanded && showCount !== undefined && items.length > showCount;
  const handleExpand = useCallback(() => {
    setExpanded(true);
  }, []);

  return (
    <Box component="ul" sx={listStyles}>
      {visibleItems.map((item) => (
        <ItemListRow {...item} key={item.id} />
      ))}
      {canExpand && (
        <Box component="li" sx={showAllRowStyles}>
          <Button
            aria-expanded={expanded}
            density="compact"
            onClick={handleExpand}
            sx={showAllButtonStyles}
            tone="neutral"
            variant="text"
          >
            <FormattedMessage id="general.showAll" defaultMessage="Show all" />
          </Button>
        </Box>
      )}
    </Box>
  );
});

ItemList.displayName = 'ItemList';

export { ItemList };
